import { useCallback, useMemo, useState } from "react";
import TspApiService from "../../api/tspApiService";
import useTspApiResource from "../../hooks/useTspApiResource";
import { adaptCategory, adaptTopic, topicListParams } from "./forumModel";

const NOTHING = () => Promise.resolve({ data: null });

// UI/API boundary for the Forum. GET /api/tsp/forum/categories + /topics do
// the search/filter/sort/pagination server-side (see forumModel.js's
// topicListParams); `actions.*` are the only way the page ever mutates
// anything, and every one calls the real endpoint and then reloads exactly
// the resources that could have changed.
export default function useTspForumData(filters, openTopicId) {
  const categories = useTspApiResource(() => TspApiService.forum.listCategories(), []);
  const topics = useTspApiResource(() => TspApiService.forum.listTopics(topicListParams(filters)), [JSON.stringify(filters)]);
  const openTopic = useTspApiResource(() => (openTopicId ? TspApiService.forum.getTopic(openTopicId) : NOTHING()), [openTopicId]);

  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async (call, { reloadTopics = true, reloadOpen = true } = {}) => {
      setBusy(true);
      try {
        const result = await call();
        if (reloadTopics) topics.reload();
        if (reloadOpen && openTopicId) openTopic.reload();
        if (reloadTopics) categories.reload(); // topic_count can change
        return result;
      } finally {
        setBusy(false);
      }
    },
    // `topics`/`categories`/`openTopic` are useTspApiResource results whose
    // identity changes every render; only re-create `run` when the topic
    // being viewed changes, same pattern as elsewhere in this hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openTopicId]
  );

  const actions = useMemo(
    () => ({
      addTopic: (input) => run(() => TspApiService.forum.createTopic({ title: input.title, body: input.body, category: input.category, topic_type: input.type })),
      addReply: (topicId, { body }) => run(() => TspApiService.forum.createReply(topicId, { body })),
      toggleTopicLike: (topicId) => run(() => TspApiService.forum.toggleLike(topicId)),
      toggleHelpful: (_topicId, replyId) => run(() => TspApiService.forum.toggleHelpful(replyId)),
      markBest: (_topicId, replyId) => run(() => TspApiService.forum.toggleBestAnswer(replyId)),
      // Pin really is a toggle server-side; subscribe/unsubscribe are two
      // endpoints, so the caller passes the CURRENT `subscribed` flag (from
      // the topic it already has) to pick the right one.
      togglePin: (topicId) => run(() => TspApiService.forum.togglePin(topicId)),
      toggleSubscribe: (topicId, subscribed) => run(() => (subscribed ? TspApiService.forum.unsubscribe(topicId) : TspApiService.forum.subscribe(topicId))),
      reportTopic: (topicId, reason = "Reported by a forum member") => run(() => TspApiService.forum.reportTopic(topicId, reason)),
      countView: (topicId) => run(() => TspApiService.forum.recordView(topicId), { reloadTopics: false }),
    }),
    [run]
  );

  // Memoized on the underlying resources' own (stable) data references, not
  // recomputed on every render — otherwise a page effect keyed on `data`
  // (e.g. accumulating pages) would see a "new" value every render and loop
  // forever, since useTspApiResource's `data`/`meta` only change identity
  // when a real fetch actually resolves.
  const data = useMemo(
    () =>
      categories.data
        ? {
            categories: categories.data.map(adaptCategory),
            topics: (topics.data || []).map(adaptTopic),
            meta: topics.meta,
            openTopic: openTopic.data ? adaptTopic(openTopic.data) : null,
          }
        : null,
    [categories.data, topics.data, topics.meta, openTopic.data]
  );

  return {
    data,
    loading: categories.loading || topics.loading || (Boolean(openTopicId) && openTopic.loading),
    // Kept separate: an open-topic 404 (a bad/removed link) is a different
    // page state from "the forum failed to load" (categories/list error).
    error: categories.error || topics.error,
    openTopicError: openTopicId ? openTopic.error : null,
    busy,
    actions,
  };
}
